// frontend/src/pages/freelancer/FreelancerProductEdit.js
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FreelancerSidebarLayout from "./FreelancerSidebarLayout";
import axios from "axios";
import { getStorefrontSetupStatus } from "../../services/storefrontService";
import ImageUploadField from "../../components/ImageUploadField";
import StorefrontSetupStatusBanner from "../../components/storefront/StorefrontSetupStatusBanner";
import VariantEditor from "../../components/VariantEditor";
import "./freelancerStyles.css";

const PRESET_DIMENSIONS = {
  sizes: {
    name: "Size",
    key: "size",
    values: [
      { label: "Extra Small (XS)", value: "xs" },
      { label: "Small (S)", value: "s" },
      { label: "Medium (M)", value: "m" },
      { label: "Large (L)", value: "l" },
      { label: "Extra Large (XL)", value: "xl" },
      { label: "2XL", value: "2xl" },
      { label: "3XL", value: "3xl" },
    ],
  },
  volumes: {
    name: "Volume",
    key: "volume",
    values: [
      { label: "10ml", value: "10ml" },
      { label: "25ml", value: "25ml" },
      { label: "50ml", value: "50ml" },
      { label: "100ml", value: "100ml" },
    ],
  },
  weights: {
    name: "Weight",
    key: "weight",
    values: [
      { label: "1 lb", value: "1lb" },
      { label: "2 lb", value: "2lb" },
      { label: "5 lb", value: "5lb" },
      { label: "10 lb", value: "10lb" },
    ],
  },
  scents: {
    name: "Scent",
    key: "scent",
    values: [
      { label: "Lavender", value: "lavender" },
      { label: "Rose", value: "rose" },
      { label: "Jasmine", value: "jasmine" },
      { label: "Vanilla", value: "vanilla" },
      { label: "Mint", value: "mint" },
      { label: "Citrus", value: "citrus" },
      { label: "Coconut", value: "coconut" },
      { label: "Unscented", value: "unscented" },
    ],
  },
  colors: {
    name: "Color",
    key: "color",
    values: [
      { label: "Black", value: "black" },
      { label: "White", value: "white" },
      { label: "Red", value: "red" },
      { label: "Blue", value: "blue" },
      { label: "Green", value: "green" },
      { label: "Pink", value: "pink" },
      { label: "Brown", value: "brown" },
      { label: "Blonde", value: "blonde" },
    ],
  },
};

function FreelancerProductEdit() {
  const navigate = useNavigate();
  const roles = JSON.parse(localStorage.getItem("ogestyle_roles") || "[]");
  const productBasePath = roles.includes("R7")
    ? "/dashboard/booth-renter/products"
    : "/dashboard/freelancer/products";
  const { productId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [platformSettings, setPlatformSettings] = useState({
    platformFeePercent: 9,
    platformFeePercentProduct: 9,
    stripeFeePercent: 2.9,
    stripeFeeFixed: 0.3,
    maxProductImagesPerProduct: 8,
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState("");
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [storefrontStatus, setStorefrontStatus] = useState(null);
  const [creationMode, setCreationMode] = useState("single");
  const [variantDimensions, setVariantDimensions] = useState([]);
  const [variants, setVariants] = useState([]);
  const hasVariants = creationMode === "multi";
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    stock: "",
    lowStockThreshold: 5,
    category: [],
    brand: "",
    sku: "",
    isActive: true,
    isFeatured: false,
    images: [],
    recommendedForServices: [],
  });
  const trimmedDescriptionLength = (formData.description || "").trim().length;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg("Configuration error.");
      return null;
    }
    return baseURL;
  }, []);

  const isStorefrontSetupIncomplete =
    storefrontStatus &&
    Array.isArray(storefrontStatus.missingRequirements) &&
    storefrontStatus.missingRequirements.length > 0;
  const onboardingStage = storefrontStatus?.onboardingStage || "hard";
  const setupUiEnabled = onboardingStage !== "off";
  const softGateEnabled = onboardingStage === "soft";

  const confirmSoftPublishWarning = (actionLabel) => {
    if (!softGateEnabled) return true;
    if (!isStorefrontSetupIncomplete) return true;
    const missing = (
      storefrontStatus.missingRequirementLabels ||
      storefrontStatus.missingRequirements ||
      []
    ).join(", ");
    return window.confirm(
      `Storefront setup is incomplete (${missing}). You can continue for now, but publishing may be restricted later. Continue to ${actionLabel}?`,
    );
  };

  const handleStoreSetupBlockedError = (err) => {
    const code = err?.response?.data?.error;
    if (code !== "STORE_SETUP_INCOMPLETE") return false;

    const message =
      err?.response?.data?.message ||
      "Complete storefront setup before activating products.";
    const blocking =
      err?.response?.data?.blockingRequirementLabels ||
      err?.response?.data?.missingRequirementLabels ||
      [];
    const reasonLine =
      Array.isArray(blocking) && blocking.length > 0
        ? `\n\nBlocked by: ${blocking.join(", ")}`
        : "";
    const setupUrl =
      err?.response?.data?.setupUrl || `${productBasePath}/storefront-setup`;

    setErrorMsg(message);
    if (window.confirm(`${message}${reasonLine}\n\nOpen storefront setup now?`)) {
      navigate(setupUrl);
    }
    return true;
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const headers = getAuthHeaders();
        const baseURL = getApiBaseUrl();
        if (!headers || !baseURL) return;

        // Fetch services
        const servicesRes = await axios.get(`${baseURL}/api/staff/services`, {
          headers,
        });
        setServices(servicesRes.data.services || []);

        // Fetch categories from API (same endpoint as Business Portal)
        try {
          const categoriesRes = await axios.get(
            `${baseURL}/api/platform-settings/product-categories`,
          );
          // API returns array directly, not wrapped in object
          const cats = Array.isArray(categoriesRes.data)
            ? categoriesRes.data
            : [];
          if (cats.length === 0) {
            setErrorMsg(
              "Failed to load product categories. Please refresh the page.",
            );
          }
          setCategories(cats);
        } catch (err) {
          setErrorMsg(
            "Failed to load product categories. Please refresh the page.",
          );
          setCategories([]);
        }

        // Fetch platform settings
        try {
          const settingsRes = await axios.get(
            `${baseURL}/api/admin/settings/public`,
          );
          if (settingsRes.data.success) {
            setPlatformSettings(settingsRes.data.settings);
          }
        } catch (err) {}
      } catch (err) {}
    };
    fetchInitialData();
  }, [getAuthHeaders, getApiBaseUrl]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const headers = getAuthHeaders();
        const baseURL = getApiBaseUrl();
        if (!headers || !baseURL) return;

        const res = await axios.get(
          `${baseURL}/api/freelancer/products/${productId}`,
          { headers },
        );
        const product = res.data.product || res.data;

        // Handle category - ensure it's an array
        const categoryValue = Array.isArray(product.category)
          ? product.category
          : product.category
            ? [product.category]
            : [];

        setFormData({
          name: product.name || "",
          description: product.description || "",
          price: product.price || "",
          stock: product.stock || "",
          lowStockThreshold: product.lowStockThreshold || 5,
          category: categoryValue,
          brand: product.brand || "",
          sku: product.sku || "",
          isActive: product.isActive !== undefined ? product.isActive : true,
          isFeatured: product.isFeatured || false,
          images: product.images || [],
          recommendedForServices: product.recommendedForServices || [],
        });

        const hasMultiVariants =
          product.hasVariants === true ||
          (Array.isArray(product.variantDimensions) &&
            product.variantDimensions.length > 0);
        setCreationMode(hasMultiVariants ? "multi" : "single");
        setVariantDimensions(
          Array.isArray(product.variantDimensions) ? product.variantDimensions : [],
        );
        setVariants(
          Array.isArray(product.variants)
            ? product.variants.map((variant, index) => ({
                id: variant.id || variant._id || `variant-${index}-${Date.now()}`,
                variantKey: variant.variantKey || "default",
                variantLabel: variant.variantLabel || "Default",
                variantAttributes: Array.isArray(variant.variantAttributes)
                  ? variant.variantAttributes
                  : [],
                variantSku: variant.variantSku || "",
                variantImages: Array.isArray(variant.variantImages)
                  ? variant.variantImages
                  : [],
                quantity:
                  parseInt(
                    variant.stock ?? variant.quantity ?? variant.quantityInStock ?? 0,
                    10,
                  ) || 0,
                price:
                  typeof variant.price === "number"
                    ? variant.price
                    : parseFloat(product.price || 0) || 0,
                variantIsActive: variant.variantIsActive !== false,
              }))
            : [],
        );
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          localStorage.removeItem("ogestyle_token");
          navigate("/login");
        } else {
          setErrorMsg("Failed to load product");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId, getAuthHeaders, getApiBaseUrl, navigate]);

  useEffect(() => {
    let mounted = true;

    const fetchStorefrontStatus = async () => {
      try {
        const data = await getStorefrontSetupStatus();
        if (mounted && data?.success) {
          setStorefrontStatus(data);
        }
      } catch (error) {}
    };

    fetchStorefrontStatus();

    return () => {
      mounted = false;
    };
  }, []);

  const handleServiceToggle = (serviceId) => {
    setFormData((prev) => {
      const current = prev.recommendedForServices || [];
      if (current.includes(serviceId)) {
        return {
          ...prev,
          recommendedForServices: current.filter((id) => id !== serviceId),
        };
      } else {
        return { ...prev, recommendedForServices: [...current, serviceId] };
      }
    });
  };

  const handleRemoveImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== index),
    }));
  };

  const uploadImage = async () => {
    if (!imageFile) return null;

    const formData = new FormData();
    formData.append("image", imageFile);

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return null;

      const res = await axios.post(
        `${baseURL}/api/freelancer/products/upload-image`,
        formData,
        { headers },
      );

      return res.data.imageUrl;
    } catch (err) {
      throw new Error("Failed to upload image");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrorMsg("");
    setSuccessMsg("");

    if (
      formData.isActive &&
      !confirmSoftPublishWarning("save active product changes")
    ) {
      setSaving(false);
      return;
    }

    // Validate category selection
    if (!formData.category || formData.category.length === 0) {
      setErrorMsg("Please select at least one category");
      setSaving(false);
      return;
    }

    const normalizedDescription = String(formData.description || "").trim();
    if (!normalizedDescription || normalizedDescription.length < 30) {
      setErrorMsg("Description is required and must be at least 30 characters.");
      setSaving(false);
      return;
    }

    const parsedPrice = parseFloat(formData.price);
    const parsedStock = parseInt(formData.stock, 10);
    const resolvedBaseStock =
      Number.isFinite(parsedStock) && parsedStock >= 0 ? parsedStock : 0;

    if (
      creationMode === "single" &&
      (!Number.isFinite(parsedPrice) || parsedPrice < 0)
    ) {
      setErrorMsg("Please enter a valid product price for single-variant products.");
      setSaving(false);
      return;
    }

    if (
      creationMode === "single" &&
      (!Number.isFinite(parsedStock) || parsedStock < 0)
    ) {
      setErrorMsg("Please enter a valid stock quantity for single-variant products.");
      setSaving(false);
      return;
    }

    const normalizedVariants = Array.isArray(variants)
      ? variants
          .map((variant, index) => {
            const fallbackVariantKey = `variant-${index + 1}`;
            const resolvedVariantKey =
              String(variant?.variantKey || "").trim() || fallbackVariantKey;
            const skuToken = resolvedVariantKey
              .replace(/[^a-zA-Z0-9]+/g, "-")
              .replace(/^-+|-+$/g, "")
              .toUpperCase();
            return {
              ...variant,
              variantKey: resolvedVariantKey,
              variantLabel:
                String(variant?.variantLabel || "").trim() ||
                `Variant ${index + 1}`,
              variantSku:
                String(variant?.variantSku || "").trim() ||
                (String(formData.sku || "").trim()
                  ? `${String(formData.sku).trim()}-${skuToken}`
                  : ""),
              price:
                variant?.price !== undefined && variant?.price !== null
                  ? parseFloat(variant.price)
                  : 0,
              quantity:
                variant?.quantity !== undefined && variant?.quantity !== null
                  ? parseInt(variant.quantity, 10) || 0
                  : 0,
            };
          })
          .filter((variant) => String(variant.variantKey || "").trim().length > 0)
      : [];

    const variantPayload =
      normalizedVariants.length > 0
        ? normalizedVariants
        : [
            {
              id: `variant-default-${Date.now()}`,
              variantKey: "default",
              variantLabel: "Default",
              variantAttributes: [],
              variantSku: "",
              variantImages: [],
              quantity: resolvedBaseStock,
              price: Number.isFinite(parsedPrice) ? parsedPrice : 0,
              variantIsActive: true,
            },
          ];

    const activeVariants = variantPayload.filter(
      (variant) => variant.variantIsActive !== false,
    );
    if (activeVariants.length === 0) {
      setErrorMsg("At least one active variant is required.");
      setSaving(false);
      return;
    }

    const variantKeySet = new Set();
    for (const variant of variantPayload) {
      const key = String(variant.variantKey || "").trim().toLowerCase();
      if (variantKeySet.has(key)) {
        setErrorMsg(`Duplicate variant key detected: ${variant.variantKey}`);
        setSaving(false);
        return;
      }
      variantKeySet.add(key);
    }

    const variantSkuSet = new Set();
    for (const variant of variantPayload) {
      const sku = String(variant.variantSku || "").trim().toLowerCase();
      if (!sku) continue;
      if (variantSkuSet.has(sku)) {
        setErrorMsg(`Duplicate variant SKU detected: ${variant.variantSku}`);
        setSaving(false);
        return;
      }
      variantSkuSet.add(sku);
    }

    const invalidPriceVariant = variantPayload.find(
      (variant) =>
        variant.variantIsActive !== false &&
        !Number.isFinite(parseFloat(variant.price)),
    );
    if (invalidPriceVariant) {
      setErrorMsg("Every active variant must have a valid price.");
      setSaving(false);
      return;
    }

    const activeVariantPrices = activeVariants
      .map((variant) => parseFloat(variant.price))
      .filter((value) => Number.isFinite(value));

    const resolvedBasePrice =
      creationMode === "single"
        ? parsedPrice
        : activeVariantPrices.length > 0
          ? Math.min(...activeVariantPrices)
          : 0;

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const payload = {
        ...formData,
        price: resolvedBasePrice,
        stock: resolvedBaseStock,
        images: (formData.images || []).slice(
          0,
          Number(platformSettings?.maxProductImagesPerProduct) || 8,
        ),
        hasVariants: creationMode === "multi",
        variantDimensions: creationMode === "multi" ? variantDimensions : [],
        variants:
          creationMode === "multi"
            ? variantPayload
            : [
                {
                  id: `variant-default-${Date.now()}`,
                  variantKey: "default",
                  variantLabel: "Default",
                  variantAttributes: [],
                  variantSku: "",
                  variantImages: [],
                  quantity: resolvedBaseStock,
                  price: Number.isFinite(parsedPrice) ? parsedPrice : 0,
                  variantIsActive: true,
                },
              ],
      };

      await axios.put(`${baseURL}/api/freelancer/products/${productId}`, payload, {
        headers,
      });

      setSuccessMsg("Product updated successfully!");
      setTimeout(() => {
        navigate(productBasePath);
      }, 1500);
    } catch (err) {
      if (handleStoreSetupBlockedError(err)) {
        return;
      }
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else {
        setErrorMsg(err?.response?.data?.message || "Failed to update product");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    setSaving(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const newStatus = !formData.isActive;
      if (newStatus && !confirmSoftPublishWarning("activate this product")) {
        setSaving(false);
        return;
      }
      await axios.patch(
        `${baseURL}/api/freelancer/products/${productId}/toggle-active`,
        { isActive: newStatus },
        { headers },
      );

      setFormData({ ...formData, isActive: newStatus });
      setSuccessMsg(
        `Product ${newStatus ? "activated" : "deactivated"} successfully!`,
      );
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      if (handleStoreSetupBlockedError(err)) {
        return;
      }
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else {
        setErrorMsg(
          err?.response?.data?.message || "Failed to update product status",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <FreelancerSidebarLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>Loading product...</p>
        </div>
      </FreelancerSidebarLayout>
    );
  }

  return (
    <FreelancerSidebarLayout>
      <div
        style={{
          width: "100%",
          maxWidth: "min(1400px, calc(100vw - 72px))",
          margin: "0 auto",
          padding: isMobile ? "15px" : "20px",
        }}
      >
        <div style={{ marginBottom: isMobile ? "20px" : "30px" }}>
          <button
            onClick={() => navigate(productBasePath)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: "none",
              border: "none",
              color: "#001d4a",
              fontSize: isMobile ? "14px" : "15px",
              fontWeight: "500",
              cursor: "pointer",
              padding: "8px 0",
              marginBottom: isMobile ? "15px" : "20px",
            }}
          >
            ← Back to Products
          </button>
          <h2
            style={{
              margin: "0 0 8px 0",
              fontSize: isMobile ? "24px" : "28px",
            }}
          >
            Edit Product
          </h2>
          <p
            style={{
              margin: 0,
              color: "#666",
              fontSize: isMobile ? "14px" : "16px",
            }}
          >
            Update product information
          </p>
        </div>

        {setupUiEnabled && (
          <StorefrontSetupStatusBanner
            statusData={storefrontStatus}
            isMobile={isMobile}
            setupPath={`${productBasePath}/storefront-setup`}
          />
        )}

        {errorMsg && (
          <div
            style={{
              background: "#fff3f3",
              border: "1px solid #c00",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, color: "#c00" }}>{errorMsg}</p>
          </div>
        )}

        {successMsg && (
          <div
            style={{
              background: "#e6ffed",
              border: "1px solid #03543f",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, color: "#03543f" }}>{successMsg}</p>
          </div>
        )}

        <div
          className="dashboard-card"
          style={{ padding: isMobile ? "20px" : "30px" }}
        >
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gap: "20px" }}>
              {/* Status Badge */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "15px",
                  background: formData.isActive ? "#e6ffed" : "#fff3f3",
                  borderRadius: "8px",
                  border: `1px solid ${formData.isActive ? "#03543f" : "#ef4444"}`,
                }}
              >
                <div>
                  <div style={{ fontWeight: "600", marginBottom: "5px" }}>
                    Product Status: {formData.isActive ? "Active" : "Inactive"}
                  </div>
                  <div style={{ fontSize: "14px", color: "#666" }}>
                    {formData.isActive
                      ? "This product is visible to customers"
                      : "This product is hidden from customers"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleToggleActive}
                  disabled={saving}
                  style={{
                    padding: "10px 20px",
                    border: `1px solid ${formData.isActive ? "#ef4444" : "#03543f"}`,
                    borderRadius: "8px",
                    background: "#fff",
                    color: formData.isActive ? "#ef4444" : "#03543f",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                  }}
                >
                  {formData.isActive ? "Deactivate" : "Activate"}
                </button>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  Product Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                  }}
                  placeholder="e.g., Hair Serum"
                />
              </div>

              {/* Product Images Upload */}
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  Product Images
                </label>
                <div
                  style={{
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    padding: "16px",
                    background: "#fafafa",
                  }}
                >
                  {/* One shared field: input + HEIC/size handling + upload + previews + remove */}
                  <ImageUploadField
                    multiple
                    max={Number(platformSettings?.maxProductImagesPerProduct) || 8}
                    value={formData.images || []}
                    onChange={(images) => setFormData((prev) => ({ ...prev, images }))}
                    endpoint={`${process.env.REACT_APP_API_BASE_URL}/api/v2/products/images`}
                    onError={(message) => setErrorMsg(message)}
                  />
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  required
                  minLength={30}
                  rows={4}
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                  }}
                  placeholder="Describe your product..."
                />
                <p
                  style={{
                    margin: "6px 0 0",
                    fontSize: "12px",
                    color:
                      trimmedDescriptionLength >= 30 ? "#059669" : "#b45309",
                  }}
                >
                  {trimmedDescriptionLength >= 30
                    ? `Minimum met (${trimmedDescriptionLength}/30 characters)`
                    : `${30 - trimmedDescriptionLength} more characters required (minimum 30)`}
                </p>
              </div>

              <div
                style={{
                  background: "#fef3c7",
                  padding: isMobile ? "16px" : "20px",
                  borderRadius: "12px",
                  marginBottom: "24px",
                }}
              >
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    marginBottom: "16px",
                    color: "#374151",
                  }}
                >
                  ⚠️ Low Stock Alert
                </h3>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                    gap: "12px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Base Price *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) =>
                        setFormData({ ...formData, price: e.target.value })
                      }
                      required={creationMode === "single"}
                      placeholder="0.00"
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Base Stock *
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.stock}
                      onChange={(e) =>
                        setFormData({ ...formData, stock: e.target.value })
                      }
                      required={creationMode === "single"}
                      placeholder="0"
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Alert Threshold
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.lowStockThreshold}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          lowStockThreshold: parseInt(e.target.value, 10) || 5,
                        })
                      }
                      placeholder="5"
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                </div>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    marginTop: "8px",
                  }}
                >
                  {creationMode === "single"
                    ? "Customers see base price and stock for single-option products."
                    : "Base price and stock are defaults for generated variants; adjust per variant as needed."}
                </p>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "20px",
                }}
              >

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "500",
                      marginBottom: "8px",
                      color: "#001d4a",
                    }}
                  >
                    Brand
                  </label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) =>
                      setFormData({ ...formData, brand: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      fontSize: "16px",
                      boxSizing: "border-box",
                    }}
                    placeholder="e.g., Olaplex, Redken, etc."
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "500",
                      marginBottom: "8px",
                      color: "#001d4a",
                    }}
                  >
                    SKU
                  </label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) =>
                      setFormData({ ...formData, sku: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      fontSize: "16px",
                      boxSizing: "border-box",
                    }}
                    placeholder="Product SKU (optional)"
                  />
                </div>
              </div>

              {creationMode === "multi" && (
                <>
                  <div
                    style={{
                      background: "#f3e8ff",
                      padding: isMobile ? "16px" : "20px",
                      borderRadius: "12px",
                      marginBottom: "24px",
                    }}
                  >
                    <h3
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        marginBottom: "12px",
                        color: "#6b21a8",
                      }}
                    >
                      💡 Quick Start: Choose preset dimensions
                    </h3>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      {Object.entries(PRESET_DIMENSIONS).map(([key, preset]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => {
                            const newDimensions = [
                              ...variantDimensions,
                              {
                                name: preset.name,
                                key: preset.key,
                                position: variantDimensions.length,
                                values: preset.values.map((v, idx) => ({
                                  ...v,
                                  position: idx,
                                  isActive: true,
                                })),
                              },
                            ];
                            setVariantDimensions(newDimensions);
                          }}
                          disabled={variantDimensions.some((d) => d.key === preset.key)}
                          style={{
                            padding: "8px 12px",
                            background: variantDimensions.some((d) => d.key === preset.key)
                              ? "#e9d5ff"
                              : "white",
                            border: "1px solid #d8b4fe",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "500",
                            cursor: variantDimensions.some((d) => d.key === preset.key)
                              ? "not-allowed"
                              : "pointer",
                            color: variantDimensions.some((d) => d.key === preset.key)
                              ? "#7c3aed"
                              : "#6b21a8",
                          }}
                        >
                          {preset.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  <VariantEditor
                    hasVariants={true}
                    variantDimensions={variantDimensions}
                    variants={variants}
                    basePrice={parseFloat(formData.price) || 0}
                    baseStock={parseInt(formData.stock, 10) || 0}
                    skuPrefix={formData.sku}
                    isMobile={isMobile}
                    lockVariantMode={true}
                    onVariantsChange={setVariants}
                    onDimensionsChange={setVariantDimensions}
                    onHasVariantsChange={() => {}}
                  />
                </>
              )}

              {/* Category Selection (Checkboxes - one or more categories) */}
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  Product Categories (Select one or more) *
                </label>
                <p
                  style={{
                    fontSize: "13px",
                    color: "#6b7280",
                    marginBottom: "12px",
                    marginTop: "4px",
                  }}
                >
                  Select categories that best describe your product
                </p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : "repeat(auto-fill, minmax(180px, 1fr))",
                    gap: "12px",
                    padding: "16px",
                    background: "#f9fafb",
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  {categories.map((cat) => {
                    const isChecked = formData.category.includes(cat.value);

                    return (
                      <label
                        key={cat.value}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "12px",
                          background: isChecked ? "#dbeafe" : "white",
                          border: isChecked
                            ? "2px solid #3b82f6"
                            : "1px solid #d1d5db",
                          borderRadius: "8px",
                          cursor: "pointer",
                          opacity: 1,
                          transition: "all 0.2s ease",
                          boxSizing: "border-box",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            const newCategories = e.target.checked
                              ? [...formData.category, cat.value]
                              : formData.category.filter(
                                  (c) => c !== cat.value,
                                );
                            setFormData({
                              ...formData,
                              category: newCategories,
                            });
                          }}
                          style={{
                            marginRight: "8px",
                            cursor: "pointer",
                            width: "16px",
                            height: "16px",
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            fontSize: "14px",
                            color: "#374151",
                            fontWeight: isChecked ? "600" : "400",
                          }}
                        >
                          {cat.icon && `${cat.icon} `}
                          {cat.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
                {formData.category.length === 0 && (
                  <p
                    style={{
                      color: "#ef4444",
                      fontSize: "13px",
                      marginTop: "8px",
                      fontWeight: "500",
                    }}
                  >
                    ⚠️ Please select at least one category
                  </p>
                )}
                {formData.category.length > 0 && (
                  <p
                    style={{
                      color: "#10b981",
                      fontSize: "13px",
                      marginTop: "8px",
                      fontWeight: "500",
                    }}
                  >
                    ✓ {formData.category.length} categor
                    {formData.category.length === 1 ? "y" : "ies"} selected
                  </p>
                )}
              </div>

              {/* Revenue Breakdown */}
              {formData.price && (
                <div
                  style={{
                    background: "#fef3c7",
                    border: "1px solid #f59e0b",
                    padding: "16px",
                    borderRadius: "8px",
                    marginBottom: "20px",
                  }}
                >
                  <div
                    style={{
                      fontWeight: "600",
                      marginBottom: "8px",
                      color: "#92400e",
                    }}
                  >
                    💰 Revenue Breakdown
                  </div>
                  {(() => {
                    const price = parseFloat(formData.price || 0);
                    const productPlatformFeePercent =
                      platformSettings.platformFeePercentProduct ?? platformSettings.platformFeePercent;
                    const stripeFee =
                      (price * platformSettings.stripeFeePercent) / 100 +
                      platformSettings.stripeFeeFixed;
                    const platformFee =
                      (price * productPlatformFeePercent) / 100;
                    const totalPlatformFee = stripeFee + platformFee;
                    const yourRevenue = price - totalPlatformFee;

                    return (
                      <>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "4px",
                          }}
                        >
                          <span style={{ fontSize: "14px" }}>
                            Selling Price:
                          </span>
                          <span style={{ fontWeight: "600", fontSize: "14px" }}>
                            ${price.toFixed(2)}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "4px",
                            color: "#dc2626",
                          }}
                        >
                          <span style={{ fontSize: "14px" }}>
                            Platform Fee (
                            {(
                              platformSettings.stripeFeePercent +
                              productPlatformFeePercent
                            ).toFixed(1)}
                            % + ${platformSettings.stripeFeeFixed}):
                          </span>
                          <span style={{ fontSize: "14px" }}>
                            -${totalPlatformFee.toFixed(2)}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            borderTop: "1px solid #f59e0b",
                            paddingTop: "8px",
                            marginTop: "8px",
                            fontWeight: "700",
                            color: "#059669",
                          }}
                        >
                          <span style={{ fontSize: "14px" }}>
                            Your Revenue:
                          </span>
                          <span style={{ fontSize: "14px" }}>
                            ${yourRevenue.toFixed(2)}
                          </span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Recommended Services Section */}
              {services.length > 0 && (
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: isMobile ? "13px" : "14px",
                      fontWeight: "500",
                      marginBottom: "12px",
                      color: "#001d4a",
                    }}
                  >
                    Recommended for Services
                  </label>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "1fr"
                        : "repeat(auto-fill, minmax(200px, 1fr))",
                      gap: "10px",
                    }}
                  >
                    {services.map((service) => (
                      <label
                        key={service._id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "10px",
                          border: "1px solid #e0e0e0",
                          borderRadius: "8px",
                          cursor: "pointer",
                          opacity: 1,
                          background: formData.recommendedForServices.includes(
                            service._id,
                          )
                            ? "#e6f7ff"
                            : "#fff",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.recommendedForServices.includes(
                            service._id,
                          )}
                          onChange={() => handleServiceToggle(service._id)}
                          style={{ cursor: "pointer" }}
                        />
                        <span style={{ fontSize: isMobile ? "13px" : "14px" }}>
                          {service.name}
                        </span>
                      </label>
                    ))}
                  </div>
                  {formData.recommendedForServices.length > 0 && (
                    <p
                      style={{
                        margin: "8px 0 0 0",
                        fontSize: "12px",
                        color: "#666",
                      }}
                    >
                      {formData.recommendedForServices.length} selected
                    </p>
                  )}
                </div>
              )}

              {/* Feature Product Checkbox */}
              <div
                style={{
                  padding: "15px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  background: "#f9f9f9",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.isFeatured}
                    onChange={(e) =>
                      setFormData({ ...formData, isFeatured: e.target.checked })
                    }
                    style={{ width: "18px", height: "18px", cursor: "pointer" }}
                  />
                  <div>
                    <div
                      style={{
                        fontWeight: "600",
                        fontSize: isMobile ? "14px" : "15px",
                        marginBottom: "2px",
                      }}
                    >
                      Feature this product
                    </div>
                    <div
                      style={{
                        fontSize: isMobile ? "12px" : "13px",
                        color: "#666",
                      }}
                    >
                      Featured products appear at the top of your product
                      listings
                    </div>
                  </div>
                </label>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: "15px",
                  marginTop: "10px",
                }}
              >
                <button
                  type="submit"
                  disabled={saving || uploadingImage}
                  className="btn-primary"
                  style={{
                    padding: isMobile ? "12px 24px" : "14px 32px",
                    fontSize: isMobile ? "15px" : "16px",
                    flex: isMobile ? "unset" : 1,
                  }}
                >
                  {uploadingImage
                    ? "Uploading Image..."
                    : saving
                      ? "Saving..."
                      : "Save Changes"}
                </button>
                <button
                  type="button"
                  onClick={() => navigate(productBasePath)}
                  style={{
                    padding: "14px 32px",
                    fontSize: "16px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    background: "#fff",
                    color: "#666",
                    fontWeight: "500",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </FreelancerSidebarLayout>
  );
}

export default FreelancerProductEdit;
